import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from "react";

/**
 * The six-digit authenticator code, as six boxes.
 *
 * People overwhelmingly paste these from a password manager or copy them off a phone, so
 * a paste of "419703" into any box has to fill all six rather than putting six characters
 * in one. Backspace on an empty box steps back, because otherwise correcting a typo means
 * reaching for the mouse.
 */
export function CodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function setDigit(index: number, digit: string) {
    const next = (value.padEnd(6, " ").slice(0, index) + digit + value.padEnd(6, " ").slice(index + 1))
      .replace(/\s/g, " ")
      .trimEnd();
    const cleaned = next.replace(/[^\d ]/g, "");
    onChange(cleaned.trimEnd());
    if (digit && index < 5) refs.current[index + 1]?.focus();
    const full = cleaned.replace(/\s/g, "");
    if (full.length === 6 && !full.includes(" ")) onComplete?.(full);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) refs.current[index + 1]?.focus();
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!digits) return;
    e.preventDefault();
    onChange(digits);
    refs.current[Math.min(digits.length, 5)]?.focus();
    if (digits.length === 6) onComplete?.(digits);
  }

  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="code-box mono"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of 6`}
          value={value[i] ?? ""}
          onChange={(e) => setDigit(i, e.target.value.replace(/\D/g, "").slice(-1))}
          onKeyDown={(e) => onKeyDown(e, i)}
          onPaste={onPaste}
        />
      ))}
    </div>
  );
}
